/**
 * Auth Error Handler Hook
 *
 * Centralized handling of 401 Unauthorized errors across the application.
 * Automatically clears session and redirects to login when authentication fails.
 */

'use client';

import { useEffect } from 'react';
import { signOut } from 'next-auth/react';
import {
  cancelAuthErrorDebounce,
  cancelPendingAuthRedirect,
  setAuthErrorDebounceTimer,
  setPendingAuthRedirectTimer
} from '@/lib/auth/pending-auth-timers';

// Re-exported so existing callers (and the LF-6403 spec) keep their import
// path; the handles themselves live in the leaf module so the jest global
// teardown can cancel them without importing next-auth (VALE-557).
export {
  cancelPendingAuthRedirect,
  cancelPendingAuthTimers
} from '@/lib/auth/pending-auth-timers';

interface AuthErrorOptions {
  /** Custom reason to pass to logout endpoint */
  reason?: string;
  /** Delay before redirect (ms) */
  redirectDelay?: number;
  /** Skip cookie clearing (for testing) */
  skipCookieClear?: boolean;
}

/**
 * Check if an error is an authentication error
 */
export function isAuthenticationError(error: unknown): boolean {
  if (!error) return false;

  const errorString = String(error);
  const errorMessage = error instanceof Error ? error.message : errorString;

  return (
    errorString.includes('401') ||
    errorString.includes('Unauthorized') ||
    errorMessage.includes('authentication') ||
    errorMessage.includes('session') ||
    errorMessage.includes('Authentication required') ||
    errorMessage.includes('Not authenticated')
  );
}

/**
 * Clear all authentication-related cookies
 * Note: For Vercel preview branches (*.vercel.app), cookies are set without a domain
 * attribute, so they're scoped to the exact host. The first clear line (no domain)
 * handles these cases.
 */
function clearAuthCookies() {
  if (typeof window === 'undefined') return;

  document.cookie.split(';').forEach((cookie) => {
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

    // Clear auth-related cookies
    if (
      name.includes('Token') ||
      name.includes('token') ||
      name.includes('tenant') ||
      name.includes('auth') ||
      name.includes('pkce') ||
      name.includes('Email') ||
      name.includes('Expiry')
    ) {
      // Clear for current domain (handles Vercel preview branches and exact host matches)
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      // Clear for parent domains (production and dev environments)
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.vale.legal`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.vale.rip`;
      // Clear for localhost
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=localhost`;
    }
  });
}

/**
 * Clear browser storage
 */
function clearStorage() {
  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch (e) {
    console.error('[AuthErrorHandler] Failed to clear storage:', e);
  }
}

/**
 * Perform logout and redirect
 */
export function handleAuthError(options: AuthErrorOptions = {}) {
  const {
    reason = 'session_expired',
    redirectDelay = 1500,
    skipCookieClear = false
  } = options;

  if (typeof window === 'undefined') return;

  console.log('[AuthErrorHandler] Authentication error detected', {
    reason,
    currentPath: window.location.pathname
  });

  // Clear cookies and storage
  if (!skipCookieClear) {
    clearAuthCookies();
    clearStorage();
  }

  // A second call (e.g. another 401 arriving while the first redirect is
  // still pending) must not stack a second signOut behind the first's.
  cancelPendingAuthRedirect();

  // Sign out via NextAuth (POST) and redirect to login
  setPendingAuthRedirectTimer(
    setTimeout(() => {
      setPendingAuthRedirectTimer(null);
      signOut({ callbackUrl: '/login', redirect: true });
    }, redirectDelay)
  );
}

/**
 * Paths whose authentication is independent of the NextAuth session
 * (token-in-URL handoff, public portal, OAuth callback). They must not be
 * reloaded out from under the user by a cross-tab `vale_login` /
 * `auth-error` broadcast — that hard-redirected the mobile handoff page
 * to /login mid-recording, which is exactly the failure mode LF-3743 is
 * supposed to avoid.
 *
 * Kept in sync with `proxy.ts` `publicPaths`, `providers-wrapper.tsx`
 * `PUBLIC_PATHS`, and `hooks/use-auth-error-handler.ts`
 * `AUTH_REDIRECT_EXEMPT_PREFIXES`.
 */
const SYNC_EXEMPT_PREFIXES = [
  // VALE-695: the impersonation landing page is visited with NO session — it
  // exists to establish one from a handoff code. Without this exemption the
  // authenticated profile fetch 401s and redirects to /login before the page
  // can redeem, which broke the handoff for the legacy token shape too.
  '/impersonate',
  '/device-recording',
  '/portal',
  '/accept-invitation',
  '/auth-error',
  '/callback',
  '/login'
];

function isSyncExemptPath(): boolean {
  if (typeof window === 'undefined') return false;
  const pathname = window.location.pathname;
  return SYNC_EXEMPT_PREFIXES.some((p) => pathname.startsWith(p));
}

/**
 * Hook for global auth error listening across tabs
 * Detects when user logs out in another tab and syncs the logout
 */
export function useAuthErrorSync() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    console.log('[AuthErrorSync] Hook mounted, listening for storage events');

    const handleStorageChange = (e: StorageEvent) => {
      // LF-3743: never reload public surfaces from a cross-tab broadcast.
      if (isSyncExemptPath()) return;
      if (e.key === 'vale_login' && e.newValue) {
        window.location.reload();
      }
    };

    // Listen for storage changes from other tabs
    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom auth error events
    const handleAuthErrorEvent = ((e: CustomEvent) => {
      // LF-3743: a 401 from somewhere else in the app must NOT yank the
      // public mobile recording route to /login.
      if (isSyncExemptPath()) return;
      console.log('[AuthErrorSync] Auth error event received', e.detail);
      handleAuthError({ reason: e.detail?.reason || 'session_expired' });
    }) as EventListener;

    window.addEventListener('auth-error', handleAuthErrorEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-error', handleAuthErrorEvent);
      // LF-6403: a redirect scheduled just before unmount must not fire
      // afterwards — it would run against a torn-down environment.
      cancelPendingAuthRedirect();
    };
  }, []);
}

/**
 * Dispatch an auth error event to notify other tabs
 * Debounced to prevent event storm from multiple concurrent 401s
 */
let authErrorCount = 0;
let lastAuthErrorReason: string = 'session_expired';

export function dispatchAuthError(reason: string = 'session_expired') {
  if (typeof window === 'undefined') return;

  authErrorCount++;
  lastAuthErrorReason = reason;

  // Clear existing timer
  cancelAuthErrorDebounce();

  // Wait 2 seconds for multiple errors to accumulate
  setAuthErrorDebounceTimer(
    setTimeout(() => {
      if (process.env.NODE_ENV === 'development') {
        console.log(
          `[AuthError] Dispatching auth error (${authErrorCount} errors accumulated)`
        );
      }

      const event = new CustomEvent('auth-error', {
        detail: {
          reason: lastAuthErrorReason,
          timestamp: Date.now(),
          errorCount: authErrorCount
        }
      });

      window.dispatchEvent(event);
      authErrorCount = 0;
      setAuthErrorDebounceTimer(null);
    }, 2000) // 2 second debounce window
  );
}
